import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ToastAction } from '@/components/ui/toast';
import { toast } from '@/hooks/useToast';
import { useI18n } from '@/hooks/useI18n';
import { saveItineraryAsTemplate } from '@/hooks/useOrphanItineraries';
import { DashboardItinerary } from '@/lib/models/dashboard';
import { SaveAsTemplatePreview } from './SaveAsTemplatePreview';
import { SaveAsTemplateNotes } from './SaveAsTemplateNotes';

interface SaveAsTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  itinerary: DashboardItinerary;
}

/**
 * Conferma del salvataggio di un itinerario come template.
 *
 * Chiede solo il titolo: la conversione delle date in giorni relativi la fa la RPC
 * `save_itinerary_as_template`, l'itinerario di partenza non viene toccato. Nessun
 * redirect automatico: il template si apre dal link nel toast di successo.
 */
export const SaveAsTemplateModal = ({
  isOpen,
  onClose,
  itinerary,
}: SaveAsTemplateModalProps) => {
  const { t } = useI18n();

  const defaultTitle = `${itinerary?.title ?? ''} (${t('itinerary.templateSuffix')})`;
  const [title, setTitle] = useState(defaultTitle);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // La modal globale resta montata durante l'animazione di chiusura: alla riapertura
  // (anche su un altro itinerario) il titolo va riportato al default.
  useEffect(() => {
    if (isOpen) {
      setTitle(defaultTitle);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, itinerary?.id]);

  const handleSave = async () => {
    const trimmed = title.trim();
    if (!trimmed) {
      setError(t('validation.titleRequired'));
      return;
    }

    setIsSaving(true);
    try {
      await saveItineraryAsTemplate(itinerary.id, trimmed);

      toast({
        variant: 'success',
        title: t('itinerary.saveAsTemplateSuccess'),
        description: t('itinerary.saveAsTemplateSuccessDesc'),
        // ModalProvider (e quindi questa modal) sta fuori dall'HashRouter: qui
        // useNavigate non esiste, si naviga scrivendo l'hash.
        action: (
          <ToastAction
            altText={t('itinerary.openInPlayground')}
            onClick={() => {
              window.location.hash = '#/playground';
            }}
          >
            {t('itinerary.openInPlayground')}
          </ToastAction>
        ),
      });

      onClose();
    } catch (err) {
      console.error('Error saving itinerary as template:', err);
      toast({
        title: t('common.error'),
        description: t('itinerary.saveAsTemplateError'),
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('itinerary.saveAsTemplate')}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isSaving ? t('common.saving') : t('itinerary.saveAsTemplateConfirm')}
          </Button>
        </>
      }
    >
      <div className="px-6 py-4 space-y-4">
        <SaveAsTemplatePreview itineraryId={itinerary.id} isOpen={isOpen} />

        <SaveAsTemplateNotes />

        <div>
          <Label htmlFor="templateTitle">{t('itinerary.saveAsTemplateTitleLabel')} *</Label>
          <Input
            id="templateTitle"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (error) setError(null);
            }}
            placeholder={t('playground.itineraryTitlePlaceholder')}
            hasError={!!error}
            disabled={isSaving}
          />
          {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
        </div>
      </div>
    </Modal>
  );
};
